import { useCallback } from 'react';
import { layoutPositionedNodeSelector } from '../flowchartLayout/constants';
import { queryBoardNode } from '../../cardBitmaps/findTaskElement';
import {
  tryCompleteMissionToSidebarDrop,
  isMissionToSidebarDragActive,
  isMissionOverSidebar,
  restoreFlowchartCanvasPointerEvents,
} from '../../../../../utils/missionToSidebarBridge';
import { isPointerOverSidebar } from '../../../../../utils/sidebarHitTest';
import { clearDropTargetHighlight } from './dropTargetFeedback';

/**
 * mouseup — sidebar / nest-on-drop, then revert canvas positions.
 * Flow layout owns placement; drag does not persist x/y.
 */
export function useFlowchartDragComplete({
  selectedNodes,
  potentialDragNode,
  hasDragged,
  setPotentialDragNode,
  setHasDragged,
  setDraggingNode,
  setDropTarget,
  draggingNodeRef,
  dropTargetRef,
  initialPositionsRef,
  currentDragPositionsRef,
}) {
  const completeDrag = useCallback((finalPointer) => {
    const currentDraggingNode = draggingNodeRef.current;
    const currentDropTarget = dropTargetRef.current;
    const draggedIds = currentDraggingNode
      ? (selectedNodes.has(currentDraggingNode) && selectedNodes.size > 1
        ? Array.from(selectedNodes)
        : [currentDraggingNode])
      : [];

    Object.entries(initialPositionsRef.current).forEach(([nodeId, pos]) => {
      const nodeElement = queryBoardNode(layoutPositionedNodeSelector(nodeId));
      if (nodeElement && pos) {
        nodeElement.style.setProperty('--node-tx', `${pos.x}px`);
        nodeElement.style.setProperty('--node-ty', `${pos.y}px`);
      }
    });

    let sidebarDropPayload = null;
    const hadSidebarDrag = isMissionToSidebarDragActive();
    const releasedOverSidebar =
      (hadSidebarDrag && isMissionOverSidebar())
      || (finalPointer?.clientX != null
        && isPointerOverSidebar(finalPointer.clientX, finalPointer.clientY));
    if (hadSidebarDrag) {
      console.log('[M2S] flow completeDrag — attempting sidebar drop', {
        finalPointer: finalPointer ? { x: finalPointer.clientX, y: finalPointer.clientY } : null,
      });
      sidebarDropPayload = tryCompleteMissionToSidebarDrop(
        finalPointer?.clientX,
        finalPointer?.clientY,
      );
      console.log('[M2S] flow completeDrag — sidebar drop result:', sidebarDropPayload);
    }

    clearDropTargetHighlight(currentDropTarget?.id);

    const sidebarInteraction = !!sidebarDropPayload || releasedOverSidebar;

    const wasDragging = currentDraggingNode !== null;
    const draggedNodeId = potentialDragNode;
    const didDrag = hasDragged;

    restoreFlowchartCanvasPointerEvents();

    setPotentialDragNode(null);
    setHasDragged(false);
    setDraggingNode(null);
    draggingNodeRef.current = null;
    initialPositionsRef.current = {};
    currentDragPositionsRef.current = {};
    dropTargetRef.current = null;
    setDropTarget(null);

    return {
      wasDragging,
      draggedNodeId,
      didDrag,
      dropTarget: sidebarInteraction ? null : currentDropTarget,
      draggedIds,
      sidebarDropPayload,
    };
  }, [
    selectedNodes,
    potentialDragNode,
    hasDragged,
    setPotentialDragNode,
    setHasDragged,
    setDraggingNode,
    setDropTarget,
    draggingNodeRef,
    dropTargetRef,
    initialPositionsRef,
    currentDragPositionsRef,
  ]);

  return { completeDrag };
}
